import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { history } from '_helpers';
import { userActions, alertActions } from '_store';
import { getIcons } from '_helpers';
import { TextField } from '@mui/material';

export { AddEdit };

/**
 * Edition / Création d'un utilisateur
 * 
 * @returns 
 */
function AddEdit() {
    const { username } = useParams();
    const [title, setTitle] = useState('Mon compte');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(x => x.users?.item);
    const users = useSelector(x => x.users?.list?.value);
    const auth = useSelector(x => x.auth.value);
    const [ isPasswordVisible, setIsPasswordVisible ] = useState(false)

    // form validation rules 
    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required('Nom requis')
            .min(3, 'Prénom trop court')
            .max(40, '40 caractères maximum'),
        lastName: Yup.string()
            .required('Prénom requis')
            .min(3, 'Nom trop court')
            .max(40, '40 caractères maximum'),
        email: Yup.string()
            .required('Adresse email requis')
            .email('Adresse email non valide'),
        username: Yup.string()
            .required('Nom d\'utilisateur requis')
            .min(3, 'Nom d\'utilisateur trop court')
            .max(40, '40 caractères maximum'),
        role: Yup.string()
            .required('Rôle requis'),
        password: Yup.string()
            .transform(x => x === '' ? undefined : x)
            // password optional in edit mode
            .concat(username ? null : Yup.string().required('Mot de passe requis'))
            .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
            .matches(/^(?=.*[A-Z])/, 'Le mot de passe doit contenir au moins une majuscule')
            .matches(/^(?=.*[a-z])/, 'Le mot de passe doit contenir au moins une minuscule')
            .matches(/^(?=.*[@$!%*?&#])/, 'Le mot de passe doit contenir au moins un caractère spécial')
            .matches(/^(?=.*[0-9])/, 'Le mot de passe doit contenir au moins un chiffre'),            
    });
    const formOptions = { resolver: yupResolver(validationSchema) };

    // get functions to build form with useForm() hook
    const { register, handleSubmit, reset, formState } = useForm(formOptions);
    const { errors, isSubmitting } = formState;

    useEffect(() => {       
        //get all users only for admins
        if (!users && auth.role === 'admin'){
            dispatch(userActions.getAll())
            const user = users?.find(x => x.username === username);
            if (!user) {
                navigate('/')
            }
        } 
    }, [])

    useEffect(() => {
        if (username && users) {
            //If user exists, get its data and populate form            
            const user = users?.find(x => x.username === username);
            username === auth.username ? setTitle('Mon compte') : setTitle('Modifier un utilisateur');
            dispatch(userActions.setUser(user))
            reset(user);
        } else if ((username && !user) || (username === auth?.username) ){
            const {access_token, token_type, ...user} = auth
            dispatch(userActions.setUser(user))
            reset(user);
        } else {
            setTitle('Ajouter un utilisateur');
        }
    }, [users]);


    async function onSubmit(data) {
        dispatch(alertActions.clear());
        try {
            // create or update user based on id param
            let message;
            if (username) {
                await dispatch(userActions.update({ username, data })).unwrap();
                message = auth.username !== username ? 'Utilisateur mis à jour' : 'Compte mis à jour';
            } else {
                await dispatch(userActions.register(data)).unwrap();
                message = 'Nouvel utilisateur créé';
            }
            // get return url depending on the user's role and user updated                 
            let url = (auth.role === "admin" && auth.username !== username) ? "/users" : `/user/${username}`;
            const { from } = history.location.state || { from: { pathname: url } }  ;
            history.navigate(from);
            dispatch(alertActions.success({ message, showAfterRedirect: true }));
        } catch (error) {
            dispatch(alertActions.error(error));
        }
    }

    return (
        <div className='py-12 px-24'>
            {!(user?.loading || user?.error) &&
            <>
            <div className="title-customtable">{title}</div>            
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="border-b border-gray-900/10 pb-12">
                        <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <label className="form-label">Nom</label>
                                <TextField size="small"  name="lastName" type="text" {...register('lastName')} className={`form-control ${errors.lastName ? 'is-invalid' : ''}`} />
                                <div className="invalid-feedback">{errors.lastName?.message}</div>
                            </div>
                            <div className="sm:col-span-3">
                                <label className="form-label">Prénom</label>
                                <TextField size="small"  name="firstName" type="text" {...register('firstName')} className={`form-control ${errors.firstName ? 'is-invalid' : ''}`} />
                                <div className="invalid-feedback">{errors.firstName?.message}</div>
                            </div>
                            <div className="sm:col-span-3">
                                <label className="form-label">Adresse email</label>
                                <TextField size="small"  name="email" type="text" {...register('email')} className={`form-control ${errors.email ? 'is-invalid' : ''}`} />
                                <div className="invalid-feedback">{errors.email?.message}</div>
                            </div>
                            {auth?.role === 'admin' && 
                            <div className="sm:col-span-3">
                                <label className="form-label">Rôle</label>
                                <select name="role" {...register('role')} className={`form-control ${errors.title ? 'is-invalid' : ''}`}>
                                    <option value=""></option>
                                    <option value="user_geolocalisation">Utilisateur Géolocalisation</option>
                                    <option value="admin">Administrateur</option>
                                    <option value="user_portal">Utilisateur Portail Bus Elec</option>
                                </select>
                                <div className="invalid-feedback">{errors.role?.message}</div>
                            </div>}
                            <div className="sm:col-span-3">
                                <label className="form-label mb-2">Nom d'utilisateur</label>
                                <TextField size="small"  disabled={!!username} name="username" type="text" autoComplete="new-password" {...register('username')} className={`form-control ${errors.username ? 'is-invalid' : ''}`} title={!!username ? "Le nom d'utilisateur ne peut pas être modifié" : ""}/>
                                <div className="invalid-feedback">{errors.username?.message}</div>
                            </div>
                            <div className="sm:col-span-3 relative">
                                <label className="form-label">
                                    Mot de passe*
                                    {username && <em className="ml-1">(Laisser blanc pour conserver le mot de passe actuel)</em>}
                                    <br />
                                    
                                </label>
                                <TextField size="small"  name="password" type={isPasswordVisible ? 'text' : 'password'} {...register('password')} autoComplete="new-password" className={`form-control ${errors.password ? 'is-invalid' : ''}`} />
                                <span className="show-password-icon  top-[43%]">
                                    <button 
                                        type="button" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsPasswordVisible(!isPasswordVisible);
                                        }}                                     
                                        >
                                        <img src={isPasswordVisible ? getIcons('visibilityOffOutlined') : getIcons('visibilityOutlined')} height={24} width={24} alt='icone oeil'/>
                                    </button>
                                </span>
                                <small><i>*Doit contenir au moins 8 caractères dont:  1 majuscule, 1 minuscule, 1 caractère spécial et 1 chiffre</i></small>
                                <div className="invalid-feedback">{errors.password?.message}</div>
                            </div>
                        </div>
                    </div>

                    <div className="btn-container">
                        <button className='btn btn-custom btn-custom-secondary'  onClick={() => navigate('/users')}>
                            <img src={getIcons("clearOutlinedBlack")} alt="icon return" className="btn-custom-icon"/>
                            Annuler
                        </button>
                        <button type="submit" disabled={isSubmitting} className="btn btn-custom btn-custom-primary">
                            {isSubmitting && <span className="spinner-border spinner-border-sm me-1"></span>}
                            <img src={getIcons("checkOutlined")} alt="icon return" className="btn-custom-icon"/>
                            Enregistrer
                        </button>
                    </div>
                </form>
                </>
            }
            {user?.loading &&
                <div className="text-center m-5">
                    <span className="spinner-border spinner-border-lg align-center"></span>
                </div>
            }
            {user?.error &&
                <div className="text-center m-5">
                    <div className="text-danger">Erreur de chargement de l'utilisateur: {user.error}</div>
                </div>
            }
        </div>
    );
}
